import { Injectable, inject } from '@angular/core';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { ComponentStore } from '@ngrx/component-store';
import { RuleEvaluation } from '../models';
import { isFunction, omit } from 'lodash';

interface IqRulesState {
    evaluators: Record<string, (condition?: any) => Promise<boolean>>;
}

@Injectable({
    providedIn: 'root',
})
export class IqRulesStateService extends ComponentStore<IqRulesState> {
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    constructor() {
        super({ evaluators: {} });
        /**
         * Registers an evaluator based on the type given and
         * a function that returns a promise of a boolean.
         *
         * The condition in the function is optional since you
         * could have an evaluator that doesn't need additional
         * information to evaluate the rule.
         */
        this._bus.on(
            'type:rules:evaluator:register',
            ({
                evaluator,
                type,
            }: {
                evaluator: (condition?: any) => Promise<boolean>;
                type: string;
            }) => {
                this.addEvaluator(type, evaluator);
            },
        );

        /**
         * Runs the evaluator based on the evaluation object given.
         *
         */
        this._bus.on(
            'type:rules:evaluator:run',
            async (evaluation: RuleEvaluation) => {
                return await this.dispatchRun(evaluation);
            },
        );
    }

    /**
     * Runs the evaluator on the given rules. If the rules resolve to true
     * it emits a success event with the rules and operator removed from the payload.
     *
     *
     * If the rules resolve to false it emits a failure event with the rules and operator removed from the payload.
     *
     * > Note: If there are no rules it will emit a success event.
     *
     * @param evaluation - an object that has an operator that is the connection for rules and the rules to evaluate.
     * @returns void
     */
    async dispatchRun(
        evaluation: RuleEvaluation = { operator: 'AND', rules: [] },
    ): Promise<void> {
        const result = await this.runEvaluator(evaluation);

        if (!result)
            return this._bus.dispatch({
                name: 'type:rules:evaluator:run:failure',
                payload: omit(evaluation, ['rules', 'operator']),
            });

        this._bus.dispatch({
            name: 'type:rules:evaluator:run:success',
            payload: omit(evaluation, ['rules', 'operator']),
        });
    }

    /**
     * Takes a type and returns a function that takes a condition and returns a promise of a boolean.
     *
     * @param type - the type of evaluator
     * @returns a function that takes a condition and returns a promise of a boolean.
     */
    getEvaluator = (type: string): ((condition?: any) => Promise<boolean>) =>
        this.selectSignal((state) => state.evaluators[type] ?? null)();

    /**
     * Registers an evaluator based on the type given. These will be used by external services to evaluate rules.
     *
     * @param type - the type of rule this is (used to identify the evaluator)
     * @param evaluator - the function that takes an optional condition and returns a promise of a boolean
     * @returns - void
     */
    addEvaluator(
        type: string,
        evaluator: (condition?: any) => Promise<boolean>,
    ): void {
        if (!type || !isFunction(evaluator) || this.getEvaluator(type)) return;

        this.patchState((state) => {
            state.evaluators[type] = evaluator;
            return state;
        });
    }

    /**
     *
     * @param evaluation
     * @returns
     */
    runEvaluator(
        evaluation: RuleEvaluation = { operator: 'AND', rules: [] },
    ): Promise<boolean> {
        if (!evaluation?.rules?.length) return Promise.resolve(true);

        return Promise.all(
            evaluation.rules.map(async (rule: { type: string }) => {
                const evaluator: (condition: any) => Promise<boolean> =
                    this.getEvaluator(rule.type);

                if (!evaluator) return Promise.resolve(false);

                return evaluator(rule);
            }),
        ).then((results) => {
            return {
                AND: results.every((result) => result),
                OR: results.some((result) => result),
                NOT: !results.every((result) => result),
            }[evaluation.operator ?? 'AND'];
        });
    }
}
